/**
 * Typed client for the Elyxeer API.
 *
 * This is the only module in the app that calls `fetch`. Everything else goes through
 * the exported functions, so headers, error shape and the 401 handling live in exactly
 * one place.
 */

import { authHeaders } from "~/lib/auth-session";
import { notifyUnauthorized } from "~/lib/auth-token";
import { notifyMembershipRequired } from "~/lib/membership";
import type { AnalyticsBatch, AnalyticsIngestResult } from "~/types/analytics";
import type {
  AuthConfig,
  AuthUser,
  OTPRequestResponse,
  SessionResponse,
  UserUpdate,
} from "~/types/auth";
import type {
  ChatDevicePlatform,
  ChatMessage,
  ChatReadResponse,
  ChatThread,
} from "~/types/chat";
import type {
  DashboardHighlightDetail,
  DashboardHighlightSummary,
} from "~/types/dashboard-highlight";
import type {
  EventDetail,
  EventPublic,
  EventQuestion,
  EventRegistrationCreated,
  EventSummary,
  PublicRegistrationAccepted,
  PublicRegistrationView,
} from "~/types/event";
import type { MembershipInvitePreview } from "~/types/membership-invite";
import type {
  ContentFormat,
  ContentTopic,
  NewsArticleDetail,
  NewsArticlePage,
  NewsFormatList,
  NewsTopicList,
} from "~/types/news";
import type {
  NotificationPage,
  NotificationReadResponse,
  NotificationUnreadCount,
} from "~/types/notification";
import type {
  ContentProgress,
  ContentProgressUpdate,
  ContinueList,
} from "~/types/progress";
import type {
  RoutineCheckIn,
  RoutineSlot,
  RoutineStreak,
} from "~/types/routine";
import type { SavedItem, SavedSlugs } from "~/types/saved";
import type { OfferingsResponse } from "~/types/shop";

/**
 * Absolute API origin. It has to be absolute: the Capacitor bundle is served from
 * `capacitor://localhost`, so a same-origin path would never reach the backend.
 */
export const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8020";

const API_PREFIX = "/api/v1";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** Attach the session token. Off for the sign-in endpoints, which have none yet. */
  authenticated?: boolean;
  /** This route is protected by the API's premium-content membership dependency. */
  membershipGated?: boolean;
  /**
   * Whether a 401 ends the session. Telemetry sets this false: a token that expired
   * between a batch being queued and being flushed must drop the batch, not sign the
   * member out from under the screen they are reading. The next real call will.
   */
  endSessionOn401?: boolean;
  signal?: AbortSignal;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
    // FastAPI's 422 returns a list of validation errors; surface the first message.
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0] as { msg?: string };
      if (first.msg) return first.msg;
    }
    return `Error ${response.status}`;
  } catch {
    return `Error ${response.status}`;
  }
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    query,
    authenticated = true,
    membershipGated = false,
    endSessionOn401 = true,
    signal,
  } = options;

  const url = new URL(`${API_PREFIX}${path}`, API_BASE);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (authenticated) Object.assign(headers, await authHeaders());

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  // A 401 on an authenticated call means the session is gone. A 401 from the sign-in
  // endpoints means "wrong code" and belongs to the form, not to the session.
  if (response.status === 401 && authenticated && endSessionOn401)
    notifyUnauthorized();
  // A 403 from an explicitly membership-gated route means the session is fine but
  // both cached entitlement sources are stale. Other domain-level 403s (an invite
  // e-mail mismatch, for example) must never alter global access.
  if (response.status === 403 && authenticated && membershipGated)
    notifyMembershipRequired();
  if (!response.ok)
    throw new ApiError(await readError(response), response.status);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ── Auth ──────────────────────────────────────────────────────────

export function fetchAuthConfig(): Promise<AuthConfig> {
  return request<AuthConfig>("/auth/config", { authenticated: false });
}

export function requestOtp(
  email: string,
  options: { captchaToken?: string; locale?: string } = {},
): Promise<OTPRequestResponse> {
  return request<OTPRequestResponse>("/auth/otp/request", {
    method: "POST",
    authenticated: false,
    body: {
      email,
      captcha_token: options.captchaToken ?? null,
      locale: options.locale ?? "cs",
    },
  });
}

export function verifyOtp(
  email: string,
  code: string,
): Promise<SessionResponse> {
  return request<SessionResponse>("/auth/otp/verify", {
    method: "POST",
    authenticated: false,
    body: { email, code },
  });
}

export function fetchMe(): Promise<AuthUser> {
  return request<AuthUser>("/auth/me");
}

export function updateMe(update: UserUpdate): Promise<AuthUser> {
  return request<AuthUser>("/auth/me", { method: "PATCH", body: update });
}

/** Permanently delete the signed-in app account. Shopify is not part of this call. */
export function deleteMe(): Promise<void> {
  return request<void>("/auth/me", { method: "DELETE" });
}

// ── Private specialist chat ──────────────────────────────────────

/** Read the newest page of the signed-in member's one private conversation. */
export function fetchChatThread(
  params: {
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  } = {},
): Promise<ChatThread> {
  return request<ChatThread>("/chat/thread", {
    query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
    membershipGated: true,
    signal: params.signal,
  });
}

/** Send a member-authored message to the Elyxeer team. */
export function sendChatMessage(body: string): Promise<ChatMessage> {
  return request<ChatMessage>("/chat/messages", {
    method: "POST",
    body: { body },
    membershipGated: true,
  });
}

/** Mark all currently visible specialist replies as read by this member. */
export function markChatRead(): Promise<ChatReadResponse> {
  return request<ChatReadResponse>("/chat/read", {
    method: "POST",
    membershipGated: true,
  });
}

/** Associate a native FCM token with the currently signed-in member. */
export function registerChatDevice(
  token: string,
  platform: ChatDevicePlatform,
): Promise<void> {
  return request<void>("/chat/devices", {
    method: "PUT",
    body: { token, platform },
    membershipGated: true,
  });
}

/** Remove this signed-in member's ownership of a native FCM token. */
export function unregisterChatDevice(token: string): Promise<void> {
  return request<void>("/chat/devices", {
    method: "DELETE",
    body: { token },
  });
}

/** Read the e-mail and lifecycle state shown by the public invite landing page. */
export function fetchMembershipInvite(
  token: string,
  signal?: AbortSignal,
): Promise<MembershipInvitePreview> {
  return request<MembershipInvitePreview>(
    `/membership-invites/${encodeURIComponent(token)}`,
    { authenticated: false, signal },
  );
}

/** Claim an invitation for the signed-in account and return its updated profile. */
export function claimMembershipInvite(token: string): Promise<AuthUser> {
  return request<AuthUser>(
    `/membership-invites/${encodeURIComponent(token)}/claim`,
    { method: "POST" },
  );
}

// ── News ──────────────────────────────────────────────────────────

export function fetchNews(params: {
  locale: string;
  /**
   * The two facets of the taxonomy. Either may be omitted, and they compose: the API
   * ANDs them, so a topic and a format together narrow to the intersection.
   */
  topic?: ContentTopic;
  format?: ContentFormat;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<NewsArticlePage> {
  const { locale, topic, format, limit, offset, signal } = params;
  return request<NewsArticlePage>("/news", {
    query: { locale, topic, format, limit, offset },
    membershipGated: true,
    signal,
  });
}

/** Every topic with its current count, in the vocabulary's own order. */
export function fetchNewsTopics(
  locale: string,
  signal?: AbortSignal,
): Promise<NewsTopicList> {
  return request<NewsTopicList>("/news/topics", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

/** Every format with its current count, in the vocabulary's own order. */
export function fetchNewsFormats(
  locale: string,
  signal?: AbortSignal,
): Promise<NewsFormatList> {
  return request<NewsFormatList>("/news/formats", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

export function fetchArticle(
  slug: string,
  locale: string,
  signal?: AbortSignal,
): Promise<NewsArticleDetail> {
  return request<NewsArticleDetail>(`/news/${encodeURIComponent(slug)}`, {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

// ── Events ────────────────────────────────────────────────────────

/**
 * Events starting inside a window, earliest first.
 *
 * The bounds are half-open — `from` inclusive, `to` exclusive — so the calendar can
 * request consecutive months without an event landing in both.
 */
export function fetchEvents(params: {
  locale: string;
  from?: Date;
  to?: Date;
  signal?: AbortSignal;
}): Promise<EventSummary[]> {
  const { locale, from, to, signal } = params;
  return request<EventSummary[]>("/events", {
    query: { locale, from: from?.toISOString(), to: to?.toISOString() },
    membershipGated: true,
    signal,
  });
}

/** The nearest event starting in the server-defined six-calendar-month window. */
export function fetchUpcomingEvent(
  locale: string,
  signal?: AbortSignal,
): Promise<EventSummary | null> {
  return request<EventSummary | null>("/events/upcoming", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

export function fetchEvent(
  slug: string,
  locale: string,
  signal?: AbortSignal,
): Promise<EventDetail> {
  return request<EventDetail>(`/events/${encodeURIComponent(slug)}`, {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

/** Events the member holds a place at and that are not over, soonest first. */
export function fetchMyEvents(
  locale: string,
  signal?: AbortSignal,
): Promise<EventSummary[]> {
  return request<EventSummary[]>("/events/mine", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

/**
 * Book a place. Idempotent — the event is the whole address, so a double tap or a
 * retry lands on the same place.
 */
export function bookEvent(
  slug: string,
  locale: string,
): Promise<EventRegistrationCreated> {
  return request<EventRegistrationCreated>(
    `/events/${encodeURIComponent(slug)}/registration`,
    { method: "PUT", query: { locale }, membershipGated: true },
  );
}

/** Give a place up. Idempotent, like `bookEvent`. */
export function cancelEventBooking(slug: string): Promise<void> {
  return request<void>(`/events/${encodeURIComponent(slug)}/registration`, {
    method: "DELETE",
    membershipGated: true,
  });
}

/** The member's own questions about one event, newest first. */
export function fetchEventQuestions(
  slug: string,
  signal?: AbortSignal,
): Promise<EventQuestion[]> {
  return request<EventQuestion[]>(
    `/events/${encodeURIComponent(slug)}/questions`,
    { membershipGated: true, signal },
  );
}

export function askEventQuestion(
  slug: string,
  locale: string,
  body: string,
): Promise<EventQuestion> {
  return request<EventQuestion>(
    `/events/${encodeURIComponent(slug)}/questions`,
    {
      method: "POST",
      query: { locale },
      body: { body },
      membershipGated: true,
    },
  );
}

// ── The public registration page ──────────────────────────────────

/** The event as the signed-out landing page shows it. No broadcast link here. */
export function fetchPublicEvent(
  slug: string,
  locale: string,
  signal?: AbortSignal,
): Promise<EventPublic> {
  return request<EventPublic>(`/public/events/${encodeURIComponent(slug)}`, {
    query: { locale },
    authenticated: false,
    signal,
  });
}

/** Book a place with an address. Always 202; the confirmation arrives by e-mail. */
export function registerPublicly(
  slug: string,
  body: {
    email: string;
    locale: string;
    captchaToken?: string;
    marketingConsent: boolean;
  },
): Promise<PublicRegistrationAccepted> {
  return request<PublicRegistrationAccepted>(
    `/public/events/${encodeURIComponent(slug)}/registrations`,
    {
      method: "POST",
      authenticated: false,
      body: {
        email: body.email,
        locale: body.locale,
        captcha_token: body.captchaToken ?? null,
        marketing_consent: body.marketingConsent,
      },
    },
  );
}

/** The place a registration link names: its status, the event, the way in. */
export function fetchPublicRegistration(
  token: string,
  signal?: AbortSignal,
): Promise<PublicRegistrationView> {
  return request<PublicRegistrationView>(
    `/public/events/registrations/${encodeURIComponent(token)}`,
    { authenticated: false, signal },
  );
}

/** Open the confirmation link. Idempotent. */
export function confirmPublicRegistration(
  token: string,
): Promise<PublicRegistrationView> {
  return request<PublicRegistrationView>(
    `/public/events/registrations/${encodeURIComponent(token)}/confirm`,
    { method: "POST", authenticated: false },
  );
}

export function cancelPublicRegistration(token: string): Promise<void> {
  return request<void>(
    `/public/events/registrations/${encodeURIComponent(token)}`,
    { method: "DELETE", authenticated: false },
  );
}

export function askPublicQuestion(
  token: string,
  body: string,
): Promise<EventQuestion> {
  return request<EventQuestion>(
    `/public/events/registrations/${encodeURIComponent(token)}/questions`,
    { method: "POST", authenticated: false, body: { body } },
  );
}

// ── Notifications ─────────────────────────────────────────────────

/**
 * The member's delivered webinar reminders, newest first.
 *
 * Locale-scoped like the rest of the content routes: a reminder belongs to one
 * language row of a webinar, so asking in the wrong language would return an empty
 * inbox rather than a translated one.
 */
export function fetchNotifications(params: {
  locale: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<NotificationPage> {
  const { locale, limit, offset, signal } = params;
  return request<NotificationPage>("/notifications", {
    query: { locale, limit, offset },
    membershipGated: true,
    signal,
  });
}

/** Just the badge number, so the dashboard never pays for a page it will not show. */
export function fetchUnreadNotificationCount(
  locale: string,
  signal?: AbortSignal,
): Promise<NotificationUnreadCount> {
  return request<NotificationUnreadCount>("/notifications/unread-count", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

/**
 * Move the read watermark to now. Idempotent — a second call reports `marked_read: 0`
 * rather than failing, so opening the list twice is not an error.
 */
export function markNotificationsRead(): Promise<NotificationReadResponse> {
  return request<NotificationReadResponse>("/notifications/read", {
    method: "POST",
    membershipGated: true,
  });
}

// ── Routine ───────────────────────────────────────────────────────

/** The doses ticked between two local days (`YYYY-MM-DD`), both inclusive. */
export function fetchRoutineCheckIns(params: {
  from: string;
  to: string;
  signal?: AbortSignal;
}): Promise<RoutineCheckIn[]> {
  const { from, to, signal } = params;
  return request<RoutineCheckIn[]>("/routine/check-ins", {
    query: { from, to },
    membershipGated: true,
    signal,
  });
}

/** Complete days in a row up to `today`, the member's local calendar day. */
export function fetchRoutineStreak(
  today: string,
  signal?: AbortSignal,
): Promise<RoutineStreak> {
  return request<RoutineStreak>("/routine/streak", {
    query: { today },
    membershipGated: true,
    signal,
  });
}

/**
 * Tick a dose off. Idempotent — the day and slot are the whole address, so a retry or
 * a double tap lands on the same row and keeps the first tap's time.
 */
export function checkRoutineDose(
  day: string,
  slot: RoutineSlot,
): Promise<RoutineCheckIn> {
  return request<RoutineCheckIn>(`/routine/check-ins/${day}/${slot}`, {
    method: "PUT",
    membershipGated: true,
  });
}

/** Untick a dose. Idempotent, like `checkRoutineDose`. */
export function uncheckRoutineDose(
  day: string,
  slot: RoutineSlot,
): Promise<void> {
  return request<void>(`/routine/check-ins/${day}/${slot}`, {
    method: "DELETE",
    membershipGated: true,
  });
}

// ── Saved for later ───────────────────────────────────────────────

/** The member's saved articles in this locale, most recently saved first. */
export function fetchSaved(params: {
  locale: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<NewsArticlePage> {
  const { locale, limit, offset, signal } = params;
  return request<NewsArticlePage>("/saved", {
    query: { locale, limit, offset },
    membershipGated: true,
    signal,
  });
}

/** Every saved slug, so the hearts light without a query per card. */
export function fetchSavedSlugs(signal?: AbortSignal): Promise<SavedSlugs> {
  return request<SavedSlugs>("/saved/slugs", { membershipGated: true, signal });
}

/** Light the heart. Idempotent. */
export function saveArticle(slug: string): Promise<SavedItem> {
  return request<SavedItem>(`/saved/${encodeURIComponent(slug)}`, {
    method: "PUT",
    membershipGated: true,
  });
}

/** Put the heart out. Idempotent. */
export function unsaveArticle(slug: string): Promise<void> {
  return request<void>(`/saved/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    membershipGated: true,
  });
}

// ── Continue where you left off ───────────────────────────────────

/** Where the member stopped in one article, or null when they never started. */
export function fetchProgress(
  slug: string,
  signal?: AbortSignal,
): Promise<ContentProgress | null> {
  return request<ContentProgress | null>(
    `/progress/${encodeURIComponent(slug)}`,
    { membershipGated: true, signal },
  );
}

/** Report where the member is. An omitted field keeps its stored value. */
export function recordProgress(
  slug: string,
  update: ContentProgressUpdate,
): Promise<ContentProgress> {
  return request<ContentProgress>(`/progress/${encodeURIComponent(slug)}`, {
    method: "PUT",
    body: update,
    membershipGated: true,
  });
}

/** Unfinished articles in this locale, most recently touched first. */
export function fetchContinue(
  locale: string,
  signal?: AbortSignal,
): Promise<ContinueList> {
  return request<ContinueList>("/progress/continue", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

// ── Dashboard highlight ──────────────────────────────────────────

/** The admin-selected visible highlight for this locale, or null when hidden. */
export function fetchDashboardHighlight(
  locale: string,
  signal?: AbortSignal,
): Promise<DashboardHighlightSummary | null> {
  return request<DashboardHighlightSummary | null>("/dashboard-highlight", {
    query: { locale },
    membershipGated: true,
    signal,
  });
}

/** Read the currently visible highlight by slug for a fullscreen detail route. */
export function fetchDashboardHighlightDetail(
  slug: string,
  locale: string,
  signal?: AbortSignal,
): Promise<DashboardHighlightDetail> {
  return request<DashboardHighlightDetail>(
    `/dashboard-highlight/${encodeURIComponent(slug)}`,
    {
      query: { locale },
      membershipGated: true,
      signal,
    },
  );
}

// ── Shop ──────────────────────────────────────────────────────────

/**
 * What the storefront sells. `items` is empty — not an error — while the shop is
 * not configured; the membership screen renders that as "shop unavailable".
 */
// ── Analytics ────────────────────────────────────────────────────────────────

/**
 * Hand a batch of product-usage events to the API.
 *
 * Two flags matter more than the payload. `membershipGated: false` keeps a beacon
 * from ever tripping the paywall downgrade — a lapsed member sitting on the paywall
 * is still a session worth counting, and the funnel is mostly made of people the
 * gate turns away. `endSessionOn401: false` keeps an expired token from signing the
 * member out because a background flush happened to be the first call to notice.
 */
export function sendAnalyticsEvents(
  batch: AnalyticsBatch,
  signal?: AbortSignal,
): Promise<AnalyticsIngestResult> {
  return request<AnalyticsIngestResult>("/analytics/events", {
    method: "POST",
    body: batch,
    membershipGated: false,
    endSessionOn401: false,
    signal,
  });
}

export function fetchOfferings(
  signal?: AbortSignal,
): Promise<OfferingsResponse> {
  return request<OfferingsResponse>("/shop/offerings", { signal });
}
